import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp, Award, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import siteConfig from '../config/siteContent';

const CaseStudiesPage = () => {
  const caseStudies = siteConfig.caseStudies;

  const keyLearnings = [
    {
      icon: TrendingUp,
      title: 'Data-Driven Decisions',
      description: 'Success comes from rigorous financial analysis and performance metrics, not assumptions.'
    },
    {
      icon: Award,
      title: 'Operational Excellence',
      description: 'Consistent execution of brand standards and service quality drives guest satisfaction and loyalty.'
    },
    {
      icon: Target,
      title: 'Strategic Focus',
      description: 'Clear positioning and targeted sales strategies are essential for sustainable revenue growth.'
    },
    {
      icon: CheckCircle,
      title: 'Disciplined Management',
      description: 'Cost control and financial discipline enable profitability even in challenging markets.'
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1711743266323-5badf42d4797)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative h-full flex flex-col items-center justify-center text-white px-8 text-center pt-24">
          <h1 className="font-playfair text-5xl md:text-7xl font-bold mb-6 tracking-wide">
            Case Studies
          </h1>
          <p className="text-xl md:text-2xl font-light max-w-3xl">
            Real-world examples of our proven track record in hotel turnarounds and performance optimization
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-4xl font-bold text-[#2B2B2B] mb-6">
            Proven Results, Measurable Impact
          </h2>
          <p className="text-xl text-[#3A2E24] leading-relaxed">
            Our case studies demonstrate our ability to transform underperforming assets into market-leading properties through strategic management, operational excellence, and financial discipline.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      {caseStudies.map((study, index) => (
        <section 
          key={index} 
          className={`py-32 px-8 ${index % 2 === 0 ? 'bg-[#F4F4F4]' : 'bg-white'}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-start ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="sticky top-32">
                  <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-6 left-6 bg-[#B89C4A] text-white px-6 py-3 rounded-lg">
                      <div className="font-bold text-sm">{study.year}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-4">
                  {study.title}
                </h2>
                <p className="text-xl text-[#B89C4A] mb-8 font-semibold">
                  {study.location}
                </p>

                {/* Challenge */}
                <div className="mb-10">
                  <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-4">
                    Challenge
                  </h3>
                  <p className="text-lg text-[#3A2E24] leading-relaxed">
                    {study.challenge}
                  </p>
                </div>

                {/* Strategy */}
                <div className="mb-10">
                  <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-4">
                    Strategy
                  </h3>
                  <ul className="space-y-3">
                    {study.strategy.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-[#B89C4A] flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-[#3A2E24]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Results */}
                <div className="mb-10">
                  <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-6">
                    Results
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {study.results.map((result, idx) => (
                      <Card key={idx} className="p-6 bg-white shadow-lg">
                        <div className="text-sm text-[#B89C4A] font-semibold mb-2">
                          {result.metric}
                        </div>
                        <div className="text-2xl font-bold text-[#2B2B2B]">
                          {result.value}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Impact */}
                <div className="bg-[#B89C4A]/10 p-6 rounded-lg border-l-4 border-[#B89C4A]">
                  <h4 className="font-semibold text-[#2B2B2B] mb-2">Impact</h4>
                  <p className="text-[#3A2E24] leading-relaxed">
                    {study.impact}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Key Learnings */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              Key Success Factors
            </h2>
            <p className="text-xl text-[#3A2E24] max-w-3xl mx-auto">
              Lessons learned from successful transformations that guide our approach to every engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyLearnings.map((learning, index) => {
              const Icon = learning.icon;
              return (
                <Card key={index} className="p-8 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="w-20 h-20 bg-[#B89C4A] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4">
                    {learning.title}
                  </h3>
                  <p className="text-[#3A2E24] leading-relaxed">
                    {learning.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-32 px-8 bg-[#2B2B2B] text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-6">
              Our Track Record
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-6xl font-bold text-[#B89C4A] mb-4 font-playfair">14</div>
              <div className="text-lg text-gray-300">Months to Profitability</div>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold text-[#B89C4A] mb-4 font-playfair">5★</div>
              <div className="text-lg text-gray-300">TripAdvisor Rating</div>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold text-[#B89C4A] mb-4 font-playfair">#1</div>
              <div className="text-lg text-gray-300">Market Ranking</div>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold text-[#B89C4A] mb-4 font-playfair">3×</div>
              <div className="text-lg text-gray-300">Award Nominations</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-8">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl text-[#3A2E24] leading-relaxed mb-12">
            Let's discuss how we can help transform your hotel's performance.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              size="lg" 
              className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link to="/contact">Get in Touch <ArrowRight className="ml-2" /></Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-[#2B2B2B] text-[#2B2B2B] hover:bg-[#2B2B2B] hover:text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link to="/services">Our Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
